import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import FavoriteButton from "@/pages/detail/components/FavoriteButton";
import ShareModal from "@/pages/detail/components/ShareModal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatViews, getCategory, getRelatedWorks, getWorkById } from "@/utils/videoUtils";

export default function Detail() {
  const { id = "" } = useParams();
  const work = getWorkById(id);
  const [shareOpen, setShareOpen] = useState(false);

  usePageMeta(
    work ? `${work.title} - 日本伦理剧剧情介绍与详情` : "作品详情 - 日本伦理剧",
    work
      ? `${work.title}（${work.originalTitle}，${work.year}，${work.type}）伦理剧情介绍：${work.synopsis}`
      : "日本伦理剧作品详情页。",
    work ? `${work.title},${work.genre},日本伦理剧,伦理剧情,${work.director}` : "日本伦理剧,作品详情",
  );

  if (!work) {
    return (
      <div className="w-full px-4 md:px-6 py-24 text-center">
        <i className="ri-film-line text-5xl text-foreground-400"></i>
        <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">未找到该作品</h1>
        <p className="mt-2 text-sm text-foreground-600">该作品可能已下线或链接有误</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-700 text-background-50 font-label text-sm hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedWorks(work, 6);
  const primaryCategory = getCategory(work.categories[0]);
  const metaList = [
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join(" / ") },
    { label: "上映年份", value: String(work.year) },
    { label: "类型", value: `${work.type} · ${work.genre}` },
  ];

  return (
    <div className="w-full">
      {/* 封面大图 */}
      <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden bg-primary-950">
        <img
          src={work.backdrop}
          alt={`${work.title} 主视觉`}
          title={`${work.title} - 日本伦理剧`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/50 to-primary-950/30"></div>
        <div className="relative z-10 w-full h-full px-4 md:px-6 flex flex-col justify-end pb-8">
          <nav aria-label="面包屑" className="text-sm font-label text-background-200/80 mb-3">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            {primaryCategory && (
              <>
                <Link to={`/category/${primaryCategory.key}`} className="hover:text-accent-400 transition-colors">
                  {primaryCategory.name}
                </Link>
                <i className="ri-arrow-right-s-line mx-1"></i>
              </>
            )}
            <span className="text-background-50">{work.title}</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50">{work.title}</h1>
          <p className="mt-2 text-sm text-background-200/90 font-label">
            {work.originalTitle} · {work.year} · {work.type}
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* 海报 */}
          <div className="w-full max-w-[220px] mx-auto lg:mx-0 lg:w-56 shrink-0">
            <div className="rounded-lg overflow-hidden border border-background-200/70 bg-primary-900 aspect-[2/3]">
              <img
                src={work.poster}
                alt={`${work.title} 海报`}
                title={`${work.title} - 日本伦理剧`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <FavoriteButton workId={work.id} />
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 bg-background-50 text-foreground-800 font-label text-sm hover:border-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-line"></i> 分享
              </button>
            </div>
          </div>

          {/* 主要信息 */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              {work.rating !== null && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-500 text-accent-950 font-label text-sm">
                  <i className="ri-star-fill text-xs"></i>
                  评分 {work.rating.toFixed(1)}
                </span>
              )}
              <span className="text-sm font-label text-foreground-600">
                {formatViews(work.views)} 人浏览
              </span>
              <span className="text-sm font-label text-foreground-600">
                <i className="ri-eye-line mr-1"></i>热度指数
              </span>
            </div>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {metaList.map((item) => (
                <div key={item.label} className="flex gap-3">
                  <dt className="w-20 shrink-0 text-sm font-label text-foreground-500">{item.label}</dt>
                  <dd className="text-sm text-foreground-900">{item.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 flex flex-wrap gap-2">
              {work.categories.map((c) => {
                const cat = getCategory(c);
                if (!cat) return null;
                return (
                  <Link
                    key={c}
                    to={`/category/${c}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-label bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                  >
                    <i className={cat.icon}></i> {cat.name}
                  </Link>
                );
              })}
              {work.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-full text-xs font-label bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <h2 className="mt-8 font-heading text-xl font-bold text-foreground-950">剧情简介</h2>
            <p className="mt-3 text-sm md:text-base text-foreground-700 leading-loose">
              {work.synopsis}
            </p>
            <p className="mt-3 text-sm md:text-base text-foreground-700 leading-loose">
              {work.description}
            </p>

            <p className="mt-6 text-xs font-label text-foreground-500">
              <i className="ri-information-line mr-1"></i>
              本站为内容浏览展示站，不提供真实播放与下载功能
            </p>
          </div>
        </div>

        {/* 相关推荐 */}
        <section className="mt-14">
          <div className="flex items-end justify-between mb-5">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">相关推荐</h2>
              <p className="mt-1 text-sm text-foreground-600">与《{work.title}》同类型或题材相近的伦理剧情作品</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {related.map((item) => (
              <WorkCard key={item.id} work={item} />
            ))}
          </div>
        </section>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} work={work} />
    </div>
  );
}